import React from 'react';

import { cn } from '../../base/utils';

import { SkeletonScope, SkeletonItem, type SkeletonGroupAnimation } from '../../skeleton/motion';

import { Skeleton } from '../Skeleton';

export interface SkeletonFormFieldProps {
  animation?: SkeletonGroupAnimation;
  /**
   * 标签宽度
   * @default 80
   */
  labelWidth?: number | string;
  /**
   * 输入框类型
   * @default 'input'
   */
  type?: 'input' | 'textarea' | 'select' | 'checkbox' | 'radio';
  /**
   * 文本域行数（仅 textarea 生效）
   * @default 3
   */
  textareaRows?: number;
}

export interface SkeletonFormProps extends React.HTMLAttributes<HTMLDivElement> {
  animation?: SkeletonGroupAnimation;
  /**
   * 表单字段数量
   * @default 4
   */
  fields?: number;
  /**
   * 字段配置
   */
  fieldConfigs?: SkeletonFormFieldProps[];
  /**
   * 布局方式
   * @default 'vertical'
   */
  layout?: 'vertical' | 'horizontal';
  /**
   * 标签宽度（水平布局时生效）
   * @default 100
   */
  labelWidth?: number;
  /**
   * 是否显示提交按钮
   * @default true
   */
  showSubmitButton?: boolean;
  /**
   * 字段间距
   * @default 4
   */
  spacing?: number;
}

/**
 * 表单字段骨架屏
 */
function SkeletonFormField({
  animation,
  labelWidth = 80,
  type = 'input',
  textareaRows = 3,
  layout = 'vertical',
}: SkeletonFormFieldProps & { layout?: 'vertical' | 'horizontal' }) {
  const renderInput = () => {
    switch (type) {
      case 'textarea':
        return (
          <Skeleton className="w-full rounded-md" style={{ height: textareaRows * 24 + 16 }} />
        );
      case 'select':
        return <Skeleton className="h-9 w-full rounded-md" />;
      case 'checkbox':
      case 'radio':
        return (
          <div className="flex items-center gap-2">
            <Skeleton className="size-4 rounded" />
            <Skeleton className="h-4 w-24 rounded" />
          </div>
        );
      default:
        return <Skeleton className="h-9 w-full rounded-md" />;
    }
  };

  if (layout === 'horizontal') {
    return (
      <SkeletonScope animation={animation}>
        <div className="flex items-start gap-4">
          <Skeleton className="h-4 shrink-0 rounded" style={{ width: labelWidth }} />
          <div className="flex-1">{renderInput()}</div>
        </div>
      </SkeletonScope>
    );
  }

  return (
    <SkeletonScope animation={animation}>
      <div className="space-y-2">
        <Skeleton className="h-4 rounded" style={{ width: labelWidth }} />
        {renderInput()}
      </div>
    </SkeletonScope>
  );
}

/**
 * 表单骨架屏组件
 */
export function SkeletonForm({
  className,
  animation,
  fields = 4,
  fieldConfigs,
  layout = 'vertical',
  labelWidth = 100,
  showSubmitButton = true,
  spacing = 4,
  ...props
}: SkeletonFormProps) {
  return (
    <SkeletonScope animation={animation}>
      <div className={cn(`space-y-${spacing}`, className)} {...props}>
        {Array.from({ length: fields }).map((_, index) => {
          const config = fieldConfigs?.[index] || {};
          return (
            <SkeletonItem key={index} index={index}>
              <SkeletonFormField
                animation={config.animation}
                layout={layout}
                labelWidth={config.labelWidth ?? labelWidth}
                type={config.type}
                textareaRows={config.textareaRows}
              />
            </SkeletonItem>
          );
        })}

        {showSubmitButton && (
          <SkeletonItem index={fields}>
            <div className={cn('pt-2', layout === 'horizontal' && `pl-[${labelWidth + 16}px]`)}>
              <Skeleton className="h-9 w-24 rounded-md" />
            </div>
          </SkeletonItem>
        )}
      </div>
    </SkeletonScope>
  );
}

export { SkeletonFormField };
